/**
 * BrewNectar Product Sticky Purchase Bar
 * Syncs with brewnectar-product-main / choose-plan form (variant, selling plan, quantity).
 */
(function () {
  const BAR_SELECTOR = '[data-bn-psb]';
  const MAIN_SELECTOR = '[data-bn-pd-section], [data-bn-cyp-section]';
  const MAX_INIT_FRAMES = 120;

  /** @type {Map<HTMLElement, { mainSection: HTMLElement | null, onScroll: () => void, onVvResize?: () => void, onVvScroll?: () => void, onWinResize?: () => void }>} */
  const barRegistry = new Map();

  let planChangeBound = false;

  function getVisualViewportOffset() {
    const vv = window.visualViewport;
    if (!vv) return 0;
    const gap = window.innerHeight - (vv.height + vv.offsetTop);
    return Math.max(0, gap);
  }

  function findMainSection(bar) {
    const targetId = bar.dataset.bnPsbMainTarget;
    if (targetId) {
      const byId = document.getElementById(targetId);
      if (byId?.matches(MAIN_SELECTOR)) return byId;
      const inside = byId?.querySelector(MAIN_SELECTOR);
      if (inside) return inside;
    }
    return document.querySelector(MAIN_SELECTOR);
  }

  function updateBarVisibility(bar) {
    if (bar.classList.contains('is-design-mode')) return;

    const threshold = parseInt(bar.dataset.bnPsbScrollThreshold, 10) || 800;
    const hideWhenOffers = bar.dataset.bnPsbHideOffers === 'true';
    const offersSelector = bar.dataset.bnPsbOffersSelector || '#offers';
    const offersEl = document.querySelector(offersSelector);

    let shouldShow = window.scrollY > threshold;

    if (shouldShow && hideWhenOffers && offersEl) {
      const rect = offersEl.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const offersVisible = rect.top < viewportHeight * 0.85 && rect.bottom > viewportHeight * 0.15;
      if (offersVisible) shouldShow = false;
    }

    bar.classList.toggle('is-visible', shouldShow);
    bar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
  }

  function applyViewportOffset(bar) {
    if (bar.dataset.bnPsbViewportFix !== 'true') return;
    const offset = getVisualViewportOffset();
    bar.style.bottom = offset > 0 ? `${offset}px` : '';
  }

  function updateBarContent(bar, detail) {
    if (!detail) return;

    const priceEl = bar.querySelector('[data-bn-psb-price]');
    const compareEl = bar.querySelector('[data-bn-psb-compare]');
    const discountEl = bar.querySelector('[data-bn-psb-discount]');
    const subtitleEl = bar.querySelector('[data-bn-psb-subtitle]');
    const ctaEl = bar.querySelector('[data-bn-psb-cta]');
    const titleEl = bar.querySelector('[data-bn-psb-title]');
    const imageEl = bar.querySelector('[data-bn-psb-image]');
    const qtyEl = bar.querySelector('[data-bn-psb-quantity]');

    const priceSuffix = bar.dataset.bnPsbPriceSuffix || '/mo';
    const oneTimeLabel = bar.dataset.bnPsbOneTimeSubtitle || 'One-time purchase';
    const subscribePrefix = bar.dataset.bnPsbSubscribePrefix || 'Subscribe ·';
    const ctaSubscribe = bar.dataset.bnPsbCtaSubscribe || 'Subscribe Now';
    const ctaOneTime = bar.dataset.bnPsbCtaOnetime || 'Buy Now';
    const soldOutLabel = bar.dataset.bnPsbSoldOut || 'Sold out';

    if (priceEl) {
      priceEl.textContent = detail.perMonth || priceEl.textContent;
      const suffix = bar.querySelector('[data-bn-psb-price-suffix]');
      if (suffix) suffix.textContent = priceSuffix;
    }

    if (compareEl) {
      if (detail.compareAt) {
        compareEl.textContent = detail.compareAt;
        compareEl.hidden = false;
      } else {
        compareEl.textContent = '';
        compareEl.hidden = true;
      }
    }

    if (discountEl) {
      if (detail.discount) {
        discountEl.textContent = detail.discount;
        discountEl.hidden = false;
      } else {
        discountEl.textContent = '';
        discountEl.hidden = true;
      }
    }

    if (subtitleEl) {
      if (detail.mode === 'one_time') {
        subtitleEl.textContent = oneTimeLabel;
      } else {
        subtitleEl.textContent = detail.planLabel
          ? `${subscribePrefix} ${detail.planLabel}`
          : subscribePrefix;
      }
    }

    if (titleEl && detail.productTitle) {
      titleEl.textContent = detail.productTitle;
    }

    if (imageEl && detail.imageUrl) {
      if (imageEl.src !== detail.imageUrl) {
        imageEl.src = detail.imageUrl;
      }
      imageEl.alt = detail.productTitle || '';
      const mediaWrapper = bar.querySelector('[data-bn-psb-media-wrapper]');
      if (mediaWrapper) mediaWrapper.hidden = false;
      imageEl.hidden = false;
    }

    if (qtyEl && document.activeElement !== qtyEl) {
      qtyEl.value = String(detail.quantity || 1);
    }

    if (ctaEl) {
      ctaEl.disabled = !detail.available;
      ctaEl.setAttribute('aria-disabled', detail.available ? 'false' : 'true');
      ctaEl.textContent = detail.available
        ? detail.mode === 'one_time'
          ? ctaOneTime
          : ctaSubscribe
        : soldOutLabel;
    }
  }

  function buildDetailFromMain(mainSection) {
    const oneTime = mainSection.querySelector('[data-bn-cyp-one-time].is-selected');
    const plan = mainSection.querySelector('[data-bn-cyp-plan].is-selected');
    const button = oneTime || plan;
    const variantInput = mainSection.querySelector('[data-bn-cyp-variant-id]');
    const sellingPlanInput = mainSection.querySelector('[data-bn-cyp-selling-plan]');
    const cta = mainSection.querySelector('[data-bn-cyp-cta]');
    const mainQty = mainSection.querySelector('.bn-cyp__form [name="quantity"]');
    const productJson = mainSection.querySelector('[data-bn-cyp-product-json]');

    let product = null;
    try {
      product = productJson ? JSON.parse(productJson.textContent) : null;
    } catch {
      product = null;
    }

    const mode = oneTime ? 'one_time' : 'subscription';
    const variantId = variantInput?.value || button?.dataset.variantId || '';
    const variant = product?.variants?.find((v) => String(v.id) === String(variantId));

    return {
      mode,
      perMonth: button?.dataset.stickyPerMonth || '',
      compareAt: button?.dataset.stickyCompareAt || '',
      discount: button?.dataset.stickyDiscount || '',
      planLabel: button?.dataset.stickyLabel || '',
      available: cta ? !cta.disabled : true,
      quantity: mainQty ? parseInt(mainQty.value, 10) || 1 : 1,
      productTitle: product?.title || '',
      imageUrl: variant?.featured_image?.src || product?.featured_image || '',
      sellingPlanId:
        sellingPlanInput && !sellingPlanInput.disabled ? sellingPlanInput.value : '',
    };
  }

  function syncQuantityFromMain(bar, mainSection) {
    const qtyEl = bar.querySelector('[data-bn-psb-quantity]');
    const mainQty = mainSection?.querySelector('.bn-cyp__form [name="quantity"]');
    if (!qtyEl || !mainQty || document.activeElement === qtyEl) return;
    qtyEl.value = mainQty.value;
  }

  function bindPlanChangeDelegate() {
    if (planChangeBound) return;
    planChangeBound = true;

    document.addEventListener('bn:plan-change', (event) => {
      const detail = event.detail;
      if (!detail) return;

      barRegistry.forEach((state, bar) => {
        if (state.mainSection && detail.section && detail.section !== state.mainSection) return;
        updateBarContent(bar, detail);
        syncQuantityFromMain(bar, state.mainSection);
      });
    });
  }

  function destroyBar(bar) {
    const state = barRegistry.get(bar);
    if (!state) return;

    window.removeEventListener('scroll', state.onScroll);
    if (state.onVvResize && window.visualViewport) {
      window.visualViewport.removeEventListener('resize', state.onVvResize);
      window.visualViewport.removeEventListener('scroll', state.onVvScroll);
    }
    if (state.onWinResize) {
      window.removeEventListener('resize', state.onWinResize);
    }

    barRegistry.delete(bar);
    bar.dataset.bnPsbInit = 'false';
  }

  function initBar(bar) {
    if (!bar || bar.dataset.bnPsbInit === 'true') return;
    bar.dataset.bnPsbInit = 'true';

    bindPlanChangeDelegate();

    const mainSection = findMainSection(bar);
    const ctaEl = bar.querySelector('[data-bn-psb-cta]');
    const qtyEl = bar.querySelector('[data-bn-psb-quantity]');

    if (window.Shopify?.designMode) {
      bar.classList.add('is-design-mode', 'is-visible');
      bar.setAttribute('aria-hidden', 'false');
    }

    if (mainSection) {
      const mainQty = mainSection.querySelector('.bn-cyp__form [name="quantity"]');

      if (qtyEl && mainQty) {
        const syncToMain = () => {
          const parsed = parseInt(qtyEl.value, 10);
          if (Number.isNaN(parsed)) return;
          mainQty.value = String(parsed);
          mainQty.dispatchEvent(new Event('change', { bubbles: true }));
        };

        qtyEl.addEventListener('change', syncToMain);
        qtyEl.addEventListener('input', syncToMain);
      }

      let frameCount = 0;
      const waitForPlan = () => {
        frameCount += 1;
        const oneTime = mainSection.querySelector('[data-bn-cyp-one-time].is-selected');
        const plan = mainSection.querySelector('[data-bn-cyp-plan].is-selected');
        const ready =
          oneTime ||
          plan ||
          mainSection.dataset.bnCypInit === 'true' ||
          frameCount >= MAX_INIT_FRAMES;

        if (ready) {
          updateBarContent(bar, buildDetailFromMain(mainSection));
          return;
        }
        requestAnimationFrame(waitForPlan);
      };
      waitForPlan();
    }

    if (ctaEl && mainSection) {
      ctaEl.addEventListener('click', () => {
        if (ctaEl.disabled) return;

        const mainCta = mainSection.querySelector('[data-bn-cyp-cta]');
        const form = mainSection.querySelector('.bn-cyp__form');
        const mainQty = mainSection.querySelector('.bn-cyp__form [name="quantity"]');

        if (qtyEl && mainQty) {
          const parsed = parseInt(qtyEl.value, 10);
          if (!Number.isNaN(parsed)) {
            mainQty.value = String(parsed);
            mainQty.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }

        if (mainCta && !mainCta.disabled) {
          mainCta.click();
        } else if (form && typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else if (form) {
          form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        }
      });
    }

    let scrollTicking = false;
    const onScroll = () => {
      if (scrollTicking) return;
      scrollTicking = true;
      requestAnimationFrame(() => {
        updateBarVisibility(bar);
        applyViewportOffset(bar);
        scrollTicking = false;
      });
    };

    const state = { mainSection, onScroll };

    if (window.visualViewport && bar.dataset.bnPsbViewportFix === 'true') {
      state.onVvResize = () => applyViewportOffset(bar);
      state.onVvScroll = () => applyViewportOffset(bar);
      state.onWinResize = () => applyViewportOffset(bar);
      window.visualViewport.addEventListener('resize', state.onVvResize);
      window.visualViewport.addEventListener('scroll', state.onVvScroll);
      window.addEventListener('resize', state.onWinResize);
    }

    barRegistry.set(bar, state);
    window.addEventListener('scroll', onScroll, { passive: true });

    updateBarVisibility(bar);
    applyViewportOffset(bar);
  }

  function initAll() {
    document.querySelectorAll(BAR_SELECTOR).forEach(initBar);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const bar = e.target.querySelector(BAR_SELECTOR);
    if (bar) initBar(bar);
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const bar = e.target.querySelector(BAR_SELECTOR);
    if (bar) destroyBar(bar);
  });
})();
